import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  api,
  ConversationStreamError,
  type ConversationRetrievalItem,
  type EntityAwarePayload,
} from "../../lib/api";
import { Composer } from "./Composer";
import { EmptyState } from "./EmptyState";
import { MessageBubble, type DisplayMessage } from "./MessageBubble";

/** Conversation anchor (the "Recall" pivot in Explore).
 *
 *  Streaming: the composer fires /api/conversation/query with
 *  Accept: text/event-stream and the route SSEs back retrieval ->
 *  token* -> done. The bubble starts in pending mode (shimmer +
 *  "Recalling..."), reveals the Sources affordance the moment the
 *  retrieval event lands, and then renders the assistant text
 *  token-by-token. */
export function Conversation({ scope }: { scope?: string } = {}) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; kind: string } | null>(null);
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, busy]);

  const send = async (text: string, opts: { retry?: boolean } = {}) => {
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    setLastQuery(text);
    const userMsgId = `tmp_user_${Date.now()}`;
    const pendingId = `tmp_pending_${Date.now()}`;
    // On retry, do NOT re-add the user-bubble: the previous user
    // message stays visible, and we slot a fresh pending assistant
    // bubble under it so the chat reads as a single retried turn.
    setMessages((prev) => [
      ...prev,
      ...(opts.retry ? [] : [{ id: userMsgId, role: "user" as const, content: text }]),
      { id: pendingId, role: "assistant" as const, content: "", pending: true },
    ]);

    let assembled = "";
    let receivedRetrieval: ConversationRetrievalItem[] = [];
    let receivedEntityAware: EntityAwarePayload | null = null;
    let resolvedConversationId = conversationId;
    let finalMessageId: string | null = null;
    let finalDegradation: string | null = null;

    const updatePending = (patch: Partial<DisplayMessage>) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === pendingId ? { ...m, ...patch } : m)),
      );
    };

    try {
      await api.conversationQueryStream(
        {
          query: text,
          conversation_id: conversationId ?? undefined,
          scope,
        },
        (event) => {
          if (event.kind === "meta") {
            if (!resolvedConversationId) {
              resolvedConversationId = event.data.conversation_id;
              setConversationId(event.data.conversation_id);
            }
          } else if (event.kind === "retrieval") {
            receivedRetrieval = event.data;
            updatePending({ retrieval: event.data });
          } else if (event.kind === "entity_aware") {
            receivedEntityAware = event.data;
            updatePending({ entityAware: event.data });
          } else if (event.kind === "token") {
            assembled += event.data.delta;
            updatePending({ content: assembled, pending: false });
          } else if (event.kind === "done") {
            finalMessageId = event.data.message_id;
            finalDegradation = event.data.degradation;
            // Server's assembled text is authoritative (handles trim
            // and any post-processing).
            assembled = event.data.response;
          } else if (event.kind === "error") {
            setError({ message: event.data.message, kind: "http" });
          }
        },
      );
    } catch (err) {
      const kind = err instanceof ConversationStreamError ? err.kind : "network";
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't reach Memso. Check your connection and tap retry.";
      setError({ message, kind });
      setMessages((prev) => prev.filter((m) => m.id !== pendingId));
      setBusy(false);
      return;
    }

    // Empty-completion fallback. The model can drop the final flush
    // (local OOM, cloud timeout, parse failure on JSON mode). Show the
    // user what we DO have (retrieved sources) plus the actual cause if
    // the server gave us one, so they can act on it instead of seeing a
    // misleading "connect Gmail" suggestion when Gmail is already
    // connected.
    const sourceCount = receivedRetrieval.length;
    const finalText = assembled.trim().length > 0
      ? assembled
      : sourceCount > 0
        ? `I found ${sourceCount} source${sourceCount === 1 ? "" : "s"} for this but the model didn't return an answer. The sources are listed below — open one to read it directly, or tap Retry above.${finalDegradation ? `\n\n_Reason: ${finalDegradation}._` : ""}`
        : `I don't have signals about that yet. The capture pipeline may still be processing recent activity, or your sources don't cover this topic. Tap Retry above to try again.${finalDegradation ? `\n\n_Reason: ${finalDegradation}._` : ""}`;

    setMessages((prev) =>
      prev.map((m) =>
        m.id === pendingId
          ? {
              id: finalMessageId ?? pendingId,
              role: "assistant",
              content: finalText,
              retrieval: receivedRetrieval,
              entityAware: receivedEntityAware,
              rating: null,
              pending: false,
            }
          : m,
      ),
    );
    setBusy(false);
  };

  return (
    <section className="flex flex-col min-h-[calc(100vh-12rem)] max-h-[calc(100vh-8rem)]">
      <div className="flex-1 overflow-y-auto px-1 -mx-1">
        {messages.length === 0 ? (
          <EmptyState onPick={send} />
        ) : (
          <ol className="space-y-3 py-2">
            {messages.map((m) => (
              <li key={m.id}>
                <MessageBubble message={m} />
              </li>
            ))}
            <div ref={endRef} />
          </ol>
        )}
        {error && (
          <div className="mt-3 rounded-xl border border-rose-300/60 dark:border-rose-700/50 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 text-sm text-rose-700 dark:text-rose-200 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold">
                {error.kind === "network" && "Couldn't reach Memso"}
                {error.kind === "timeout" && "Memso is taking too long"}
                {error.kind === "http" && "Memso returned an error"}
                {error.kind === "parse" && "Memso sent unexpected data"}
              </div>
              <div className="text-xs opacity-90 mt-0.5 break-words">{error.message}</div>
            </div>
            {lastQuery && (
              <button
                type="button"
                onClick={() => send(lastQuery, { retry: true })}
                disabled={busy}
                className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
                Retry
              </button>
            )}
          </div>
        )}
      </div>
      <Composer busy={busy} onSend={send} />
    </section>
  );
}
